import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { EditRecord } from "lib/@components/edit-record"
import { updateAssetRegister } from "routes/asset-register/controller/update-asset-register"
import { TargetGuard } from "lib/@components/target-guard"
import { Bound } from "lib/@components/binding/Bound"
import { useEffect, useRef, useState } from "react"
import { useTarget } from "lib/@hooks/use-target"
import noop from "lib/noop"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { MappingSettingsWizard } from "routes/asset-register/plugins/asset-mapping/plugins/mapping-settings-wizard/mapping-settings-wizard.js"
import { useIsAnythingBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-anything-being-mapped"
import { useIsAnythingRefining } from "routes/asset-register/plugins/asset-mapping/use-is-anything-refining"
import { MobiliserHeader } from "routes/asset-register/plugins/asset-mapping/mobiliser-header.js"
import { LegacySettingsDialog } from "routes/asset-register/plugins/asset-mapping/legacy-settings-dialog"
import { LegacyInnerSettings } from "routes/asset-register/plugins/asset-mapping/legacy-inner-settings"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING } from "routes/asset-register/plugins/asset-mapping/feature-flag-mobiliser-advanced-mapping.runtime"
import { Else } from "lib/@components/else.js"
import { If } from "lib/@components/switch.js"
import { MOBILISER_STAGE_PROCESSING_TYPES } from "routes/asset-register/plugins/asset-mapping/mobiliser-stage.mjs"
import { applyMappingSettings } from "routes/asset-register/plugins/asset-mapping/controller/apply-mapping-settings"
import { getAssetCount } from "routes/asset-register/controller/get-asset-count"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"

const ALREADY_PROCESSING =
    "Mobiliser is already processing this register. Apply your settings again once it has finished."

export function MappingSettings({ children, alwaysOpen, canContinue = noop }) {
    const [hasConfiguredSettings, setHasConfiguredSettings] = useBoundValue("options.hasConfiguredSettings")
    const [hideSettings, setHideSettings] = useState(false)
    const [mobiliserStage, setMobiliserStage] = useBoundValue("options.mobiliserStage")
    const currentOptions = useRef()
    const target = useTarget()
    const isMapping = useIsAnythingBeingMapped()
    const isRefining = useIsAnythingRefining()
    const showConfiguredContent = (hasConfiguredSettings || hideSettings) && !alwaysOpen
    const mobiliserStatus = getMobiliserStatus({ hasConfiguredSettings, mobiliserStage, isMapping, isRefining })
    const { isManualMapTab, refreshRegister = noop } = useBoundContext()
    const upgradesEnabled = isEnabled(FEATURE_FLAG_MOBILISER_ADVANCED_MAPPING)

    useEffect(() => {
        canContinue(true)
    }, [canContinue])

    if (isManualMapTab) return <>{children}</>
    return (
        <>
            <If truthy={showConfiguredContent}>
                <Bound setHideSettings={setHideSettings}>
                    <If value={upgradesEnabled}>
                        <MobiliserHeader status={mobiliserStatus} showSettingsButton={showConfiguredContent} />
                        <Else>
                            <LegacySettingsDialog />
                        </Else>
                    </If>
                    {children}
                </Bound>
                <Else>
                    <Bound saveSettings={saveSettings} setHideSettings={setHideSettings}>
                        <TargetGuard notEmpty>
                            <EditRecord field="options" store={storeOptions} initialise>
                                <If value={upgradesEnabled}>
                                    <MappingSettingsWizard onFinish={onSettingsWizardFinish} />
                                    <Else>
                                        <LegacyInnerSettings />
                                    </Else>
                                </If>
                            </EditRecord>
                        </TargetGuard>
                    </Bound>
                </Else>
            </If>
        </>
    )

    async function onSettingsWizardFinish() {
        const saved = await saveSettings()
        if (!saved) return
        setHideSettings(true)
    }

    async function saveSettings(editedOptions) {
        const registerId = currentOptions.current?.registerId ?? target._id
        const options = editedOptions ?? currentOptions.current?.options ?? target.options
        if (!upgradesEnabled) return legacySaveSettings(registerId, options)
        const nextOptions = { ...options, hasConfiguredSettings: true }
        if (upgradesEnabled) nextOptions.mobiliserStage = MOBILISER_STAGE_PROCESSING_TYPES
        let saved = true
        try {
            if (!(await hasAssets(registerId))) return false
            await updateAssetRegister({ registerId, options: nextOptions })
            if (!(await startTypeProcessing(registerId))) {
                warningSnackbar(ALREADY_PROCESSING)
                return false
            }
            currentOptions.current = null
        } catch (e) {
            saved = false
            console.error(e.message)
        }
        if (saved) {
            setHasConfiguredSettings(true)
            if (upgradesEnabled) setMobiliserStage(MOBILISER_STAGE_PROCESSING_TYPES)
            refreshRegister()
        }
        return saved
    }

    async function storeOptions(options, registerId) {
        currentOptions.current = { options, registerId }
    }

    function getMobiliserStatus({ hasConfiguredSettings, mobiliserStage, isMapping, isRefining }) {
        if (!hasConfiguredSettings) return "not-started"
        if (mobiliserStage === MOBILISER_STAGE_PROCESSING_TYPES || isMapping || isRefining) return "in-progress"
        return "complete"
    }

    async function legacySaveSettings(registerId, options) {
        let saved = true
        try {
            await updateAssetRegister({ registerId, options: { ...options, hasConfiguredSettings: true } })
            currentOptions.current = null
        } catch (e) {
            saved = false
            console.error(e.message)
        }
        setHasConfiguredSettings(true)
        if (saved) {
            applyMappingSettings(registerId).catch(console.error)
        }
        return saved
    }

    async function startTypeProcessing(registerId) {
        return applyMappingSettings(registerId)
    }

    async function hasAssets(registerId) {
        const hasAssets = Number(await getAssetCount(registerId)) > 0
        if (!hasAssets) {
            warningSnackbar("Upload assets to this register before starting mapping.")
        }
        return hasAssets
    }
}
